import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { PlaybackSpec, SoundId } from '@audigoal/core';
import {
  AudioEngine,
  type EngineState,
  type PlayOptions,
  type PlaybackHandle,
  type UiSoundId,
  type UiSoundParams,
} from '@audigoal/audio';
import { useAudioSettings } from '@/stores/audio-settings';

/**
 * React face of the audio engine. One provider near the root installs the
 * gesture unlock listeners, mirrors engine state into React and pushes the
 * user's volume settings into the mixer.
 */

export interface AudioApi {
  engine: AudioEngine;
  state: EngineState;
  /** True once the context is running and prompts may autoplay. */
  unlocked: boolean;
  ensureStarted: () => Promise<void>;
  play: (spec: PlaybackSpec, options?: PlayOptions) => PlaybackHandle;
  playNote: (sound: SoundId, midi: number, options?: { dur?: number; vel?: number }) => void;
  strum: (
    sound: SoundId,
    midis: readonly number[],
    options?: { dir?: 'down' | 'up'; spreadMs?: number },
  ) => void;
  ui: (sound: UiSoundId, params?: UiSoundParams) => void;
  preload: (sounds: readonly SoundId[]) => Promise<void>;
  stopAll: () => void;
  /**
   * Whether every one of these sounds has a real sample set in this
   * deployment. False until the manifest has been read, and false for good
   * when it is missing, in which case prompts play through the synthesised
   * voices and questions whose answer is the timbre are not asked.
   */
  hasSamples: (sounds: readonly SoundId[]) => boolean;
}

const AudioContextReact = createContext<AudioApi | null>(null);

export function AudioProvider({
  children,
  engine: injected,
}: {
  children: ReactNode;
  engine?: AudioEngine;
}) {
  // The engine is a singleton; a lazy state initializer pins one instance for
  // the life of the provider without reading a ref during render.
  const [engine] = useState(() => injected ?? AudioEngine.get());
  const [state, setState] = useState<EngineState>(engine.state);
  const settings = useAudioSettings();

  useEffect(() => {
    const off = engine.onStateChange(setState);
    const cleanup = engine.installUnlockListeners();
    return () => {
      off();
      cleanup();
    };
  }, [engine]);

  useEffect(() => {
    engine.setMuted(settings.muted);
    engine.setVolume('music', settings.musicVolume);
    engine.setVolume('ui', settings.effectsVolume);
    engine.setVolume('click', settings.metronomeVolume);
  }, [
    engine,
    settings.muted,
    settings.musicVolume,
    settings.effectsVolume,
    settings.metronomeVolume,
  ]);

  const ensureStarted = useCallback(() => engine.ensureStarted(), [engine]);

  // Which instrument sample sets this deployment ships, from the manifest the
  // fetch script writes. Read once at boot, so a lesson can be planned knowing
  // whether "which instrument is playing?" would sound like the instrument it
  // names. The sampler reads the same manifest again when it loads a sound;
  // this copy exists only to answer before any sound has been asked for.
  const [sampled, setSampled] = useState<ReadonlySet<string> | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let sounds = new Set<string>();
      try {
        const response = await fetch('/samples/manifest.json');
        if (response.ok) {
          const manifest = (await response.json()) as { instruments?: Record<string, unknown> };
          sounds = new Set(Object.keys(manifest.instruments ?? {}));
        }
      } catch {
        // No manifest, no samples: the synthesised voices carry the app.
      }
      if (!cancelled) setSampled(sounds);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const api = useMemo<AudioApi>(
    () => ({
      engine,
      state,
      unlocked: state === 'running',
      ensureStarted,
      play: (spec, options) => engine.play(spec, options),
      playNote: (sound, midi, options) => engine.playNote(sound, midi, options),
      strum: (sound, midis, options) => engine.strum(sound, midis, options),
      ui: (sound, params) => engine.ui(sound, params),
      preload: (sounds) => engine.preload(sounds),
      stopAll: () => engine.stopAll(),
      hasSamples: (sounds) => sampled !== null && sounds.every((sound) => sampled.has(sound)),
    }),
    [engine, state, ensureStarted, sampled],
  );

  return <AudioContextReact.Provider value={api}>{children}</AudioContextReact.Provider>;
}

export function useAudio(): AudioApi {
  const value = useContext(AudioContextReact);
  if (value === null) throw new Error('useAudio must be used inside <AudioProvider>');
  return value;
}

/**
 * The engine if there is one, or null.
 *
 * For leaf components that make a sound as a flourish — a button's tap, a
 * sheet's whoosh. They are rendered in Storybook and in unit tests without a
 * provider, and a missing click is not worth a thrown error.
 */
export function useMaybeAudio(): AudioApi | null {
  return useContext(AudioContextReact);
}

/** Whether audio can play without another gesture. */
export function useAudioUnlocked(): boolean {
  return useAudio().unlocked;
}

export interface PlaybackControls {
  /** `rate` below 1 slows the tempo; the "slow" toggle passes 0.5. */
  play: (rate?: number) => Promise<void>;
  stop: () => void;
  playing: boolean;
  cursorBeat: number | null;
  plays: number;
  replaysLeft: number;
}

/**
 * Manages one playback at a time for a prompt: play, stop, cursor position and
 * replay counting. Changing the prompt resets the count and stops the sound;
 * so does unmounting, so a navigated-away prompt goes quiet.
 */
export function usePlayback(
  spec: PlaybackSpec | null,
  options: { maxReplays?: number } = {},
): PlaybackControls {
  const audio = useAudio();
  const handleRef = useRef<PlaybackHandle | null>(null);
  const [playing, setPlaying] = useState(false);
  const [cursorBeat, setCursorBeat] = useState<number | null>(null);
  const [plays, setPlays] = useState(0);
  const [countedSpec, setCountedSpec] = useState(spec);
  const maxReplays = options.maxReplays ?? Infinity;

  // A new prompt starts with a fresh replay budget. Adjusting state during
  // render is React's own answer to "reset state when a prop changes".
  if (countedSpec !== spec) {
    setCountedSpec(spec);
    setPlays(0);
  }

  // Warms the instrument as soon as the prompt exists. `play` waits for the
  // samples so the first hearing is never the synthesised stand-in, and a
  // soundfont is a couple of megabytes; starting here means that wait is
  // usually over before anything is pressed. Loading a sound twice is free —
  // the sampler keeps what it has.
  const { preload } = audio;
  useEffect(() => {
    if (spec === null) return;
    void preload([spec.instrument]);
  }, [preload, spec]);

  const stop = useCallback(() => {
    handleRef.current?.stop();
    handleRef.current = null;
    setPlaying(false);
    setCursorBeat(null);
  }, []);

  const play = useCallback(
    async (rate = 1) => {
      if (spec === null || plays >= maxReplays + 1) return;
      await audio.ensureStarted();
      // The prompt's instrument has to be the real one before it sounds. The
      // sampler otherwise plays a synthesised stand-in while the samples load,
      // and for "which instrument is playing?" the stand-in is the wrong
      // answer. Already-loaded sounds resolve at once; a missing sample set
      // resolves to the synth at once too, so nothing waits on the network
      // longer than the download itself.
      await audio.preload([spec.instrument]);
      handleRef.current?.stop();
      const slowed: PlaybackSpec = rate === 1 ? spec : { ...spec, tempo: spec.tempo * rate };
      setPlaying(true);
      setCursorBeat(null);
      setPlays((n) => n + 1);
      handleRef.current = audio.play(slowed, {
        onCursor: setCursorBeat,
        onEnd: () => {
          setPlaying(false);
          setCursorBeat(null);
        },
      });
    },
    [audio, spec, plays, maxReplays],
  );

  // Silence on prompt change and on unmount. The cleanup touches the handle
  // only; the engine's `onEnd` puts React state back.
  useEffect(
    () => () => {
      handleRef.current?.stop();
      handleRef.current = null;
    },
    [spec],
  );

  return {
    play,
    stop,
    playing,
    cursorBeat,
    plays,
    replaysLeft: Number.isFinite(maxReplays) ? Math.max(0, maxReplays + 1 - plays) : Infinity,
  };
}
